import { maxWeightMatching, type WeightedEdge } from '../matching/blossom.js';
import { isPlayedResult, pointsFor, type PointSystem } from '../model/types.js';
import { playedColours } from './colour.js';
import type { Pair, PairingEngine, PairingPlayer, PairingRequest, PairingResult } from './types.js';

/**
 * Arena: a time-boxed event where players are paired again as soon as they
 * finish. In the round model each "round" is a pairing wave over the players
 * who are free (available) at that moment.
 *
 * - Players are paired with the closest arena score (then rating), so the
 *   pairing is a maximum matching that minimises the total rank distance.
 * - A rematch of one of the last `avoidRecent` opponents is avoided when any
 *   other complete pairing exists.
 * - With an odd number of free players, the lowest-ranked one who waited
 *   least recently waits for the next wave (no bye points: arena players
 *   score only by playing).
 * - Colours: the player with fewer whites gets White, then alternation.
 */

export interface ArenaScoring {
  readonly win: number;
  readonly draw: number;
  readonly loss: number;
  /** Consecutive wins after which wins and draws count double ("on fire"). */
  readonly streak: number;
}

export const ARENA_SCORING: ArenaScoring = { win: 2, draw: 1, loss: 0, streak: 2 };

/**
 * Arena score from a game history: wins 2, draws 1; after `streak`
 * consecutive wins every further game counts double until a game is not won.
 * Unplayed rounds (waiting, byes, forfeits) score their normal points without
 * the streak and do not break it.
 */
export function arenaScore(
  history: PairingPlayer['history'],
  points: PointSystem,
  scoring: ArenaScoring = ARENA_SCORING,
): number {
  let score = 0;
  let run = 0;
  for (const e of history) {
    const p = pointsFor(e.result, points);
    const value = p >= points.win ? scoring.win : p > points.loss ? scoring.draw : scoring.loss;
    if (!isPlayedResult(e.result)) {
      if (e.result === '+') score += scoring.win;
      continue;
    }
    score += run >= scoring.streak ? value * 2 : value;
    run = p >= points.win ? run + 1 : 0;
  }
  return score;
}

const bits = (x: number): bigint => BigInt(Math.max(1, Math.ceil(Math.log2(x + 1))));

export class ArenaEngine implements PairingEngine {
  readonly id = 'arena';
  readonly name = 'Arena (pair on finish)';

  constructor(
    private readonly avoidRecent = 2,
    private readonly scoring: ArenaScoring = ARENA_SCORING,
  ) {}

  pair(request: PairingRequest): PairingResult {
    const score = new Map(
      request.players.map((p) => [p.id, arenaScore(p.history, request.pointSystem, this.scoring)]),
    );
    const free = request.players
      .filter((p) => p.available)
      .sort(
        (a, b) =>
          (score.get(b.id) as number) - (score.get(a.id) as number) ||
          b.rating - a.rating ||
          a.id - b.id,
      );
    const notes: string[] = [];
    let pool = free;
    if (pool.length % 2 === 1) {
      // The lowest-ranked player who has waited least recently sits out.
      const lastWait = (p: PairingPlayer): number => {
        for (let r = p.history.length - 1; r >= 0; r--) {
          const e = p.history[r] as PairingPlayer['history'][number];
          if (e.opponent === null && !isPlayedResult(e.result)) return r;
        }
        return -1;
      };
      const waiter = [...pool]
        .reverse()
        .reduce((best, p) => (lastWait(p) < lastWait(best) ? p : best));
      pool = pool.filter((p) => p !== waiter);
      notes.push(`${waiter.id} waits for the next wave`);
    }
    const n = pool.length;
    const recent = (p: PairingPlayer): Set<number> => {
      const out = new Set<number>();
      for (let r = p.history.length - 1; r >= 0 && out.size < this.avoidRecent; r--) {
        const o = (p.history[r] as PairingPlayer['history'][number]).opponent;
        if (o !== null) out.add(o);
      }
      return out;
    };
    const recents = new Map(pool.map((p) => [p.id, recent(p)]));
    const cw = bits(n + 1);
    const dw = bits(n * n + 1);
    const edges: WeightedEdge[] = [];
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const a = pool[i] as PairingPlayer;
        const b = pool[j] as PairingPlayer;
        const rematch =
          (recents.get(a.id) as Set<number>).has(b.id) ||
          (recents.get(b.id) as Set<number>).has(a.id);
        // [pairs][no recent rematch][closeness]
        const w = (((1n << cw) + (rematch ? 0n : 1n)) << dw) + BigInt(n * n - (j - i) * (j - i));
        edges.push({ u: i, v: j, w });
      }
    }
    const mate = maxWeightMatching(n, edges, { maxCardinality: true });
    const pairs: Pair[] = [];
    for (let i = 0; i < n; i++) {
      const m = mate[i] as number;
      if (m > i) {
        const a = pool[i] as PairingPlayer;
        const b = pool[m] as PairingPlayer;
        if ((recents.get(a.id) as Set<number>).has(b.id)) {
          notes.push(`${a.id}-${b.id} meet again: no other pairing of the free players`);
        }
        pairs.push(colours(a, b));
      }
    }
    return {
      pairs,
      byes: [],
      notes,
      explanation: [
        `Free players by arena score: ${free.map((p) => `${p.id} (${score.get(p.id)})`).join(', ')}.`,
        ...notes,
      ],
    };
  }
}

function colours(a: PairingPlayer, b: PairingPlayer): Pair {
  const ca = playedColours(a.history);
  const cb = playedColours(b.history);
  const whites = (cs: readonly string[]): number => cs.filter((c) => c === 'w').length;
  const da = whites(ca) - (ca.length - whites(ca));
  const db = whites(cb) - (cb.length - whites(cb));
  if (da !== db) return da < db ? { white: a.id, black: b.id } : { white: b.id, black: a.id };
  const la = ca[ca.length - 1];
  const lb = cb[cb.length - 1];
  if (la !== lb) {
    if (la === 'b' || lb === 'w') return { white: a.id, black: b.id };
    return { white: b.id, black: a.id };
  }
  return { white: a.id, black: b.id };
}
